import {
  listUploadedWeekPeriods,
  loadWeeklyLineChunkRows,
  normalizeVoucherType,
} from "../features/salesDataImport/services/salesLineChunksReader";

export const BILLING_FIND_VOUCHER_TYPE = "Outward Supply";
export const PURCHASE_FIND_VOUCHER_TYPE = "Inward Supply";

/** Exact voucher type match (normalized title case vs export casing). */
export function isExactVoucherTypeRow(row, voucherTypeLabel) {
  const expected = String(voucherTypeLabel || "").trim().toLowerCase();
  if (!expected) return false;
  const vt = normalizeVoucherType(row).toLowerCase();
  const compact = vt.replace(/[\s_-]/g, "");
  const expectedCompact = expected.replace(/[\s_-]/g, "");
  return vt === expected || compact === expectedCompact;
}

export function createExactVoucherTypeMatcher(voucherTypeLabel) {
  return (row) => isExactVoucherTypeRow(row, voucherTypeLabel);
}

function matchesSupplyType(row, kind) {
  if (kind === "outward") {
    return isExactVoucherTypeRow(row, BILLING_FIND_VOUCHER_TYPE);
  }
  const vt = normalizeVoucherType(row).toLowerCase();
  const compact = vt.replace(/[\s_-]/g, "");
  return (
    vt === "inward supply" ||
    compact === "inwardsupply" ||
    (vt.includes("inward") && vt.includes("supply"))
  );
}

/** Exact "Outward Supply" only — excludes e.g. Outward Supply Br, Outward Supply Spareway. */
export function isOutwardSupplyRow(row) {
  return matchesSupplyType(row, "outward");
}

export function isInwardSupplyRow(row) {
  return isExactVoucherTypeRow(row, PURCHASE_FIND_VOUCHER_TYPE);
}

export async function forEachVoucherTypeRowFromSalesImport(onRow, voucherTypeLabel, { maxWeeks = 8 } = {}) {
  const matchRow = createExactVoucherTypeMatcher(voucherTypeLabel);
  return forEachSupplyRowFromSalesImport(onRow, { maxWeeks, matchRow });
}

/**
 * Stream Outward Supply lines week-by-week (avoids holding 24×30k rows in memory).
 * @param {(row: object, meta: object) => void} onRow
 */
async function forEachSupplyRowFromSalesImport(onRow, { maxWeeks = 8, matchRow } = {}) {
  const weeks = (await listUploadedWeekPeriods()).slice(0, maxWeeks);
  let matchCount = 0;
  for (const meta of weeks) {
    let part = [];
    try {
      part = await loadWeeklyLineChunkRows(meta.year, meta.monthIndex, meta.weekOfMonth);
    } catch (weekErr) {
      console.warn("Sales import week load failed", meta.periodKey, weekErr);
      continue;
    }
    for (const row of part) {
      if (matchRow(row)) {
        onRow(row, meta);
        matchCount += 1;
      }
    }
    await new Promise((r) => setTimeout(r, 0));
  }
  return { weeksLoaded: weeks.length, matchCount };
}

export async function forEachOutwardSupplyRowFromSalesImport(onRow, { maxWeeks = 8 } = {}) {
  return forEachSupplyRowFromSalesImport(onRow, { maxWeeks, matchRow: isOutwardSupplyRow });
}

export async function forEachInwardSupplyRowFromSalesImport(onRow, { maxWeeks = 8 } = {}) {
  return forEachSupplyRowFromSalesImport(onRow, { maxWeeks, matchRow: isInwardSupplyRow });
}

/** @deprecated Prefer forEachOutwardSupplyRowFromSalesImport for large catalogs. */
export async function fetchOutwardSupplyRowsFromSalesImport({ maxWeeks = 8, onWeekLoaded } = {}) {
  const rows = [];
  const { weeksLoaded, matchCount } = await forEachOutwardSupplyRowFromSalesImport(
    (row, meta) => {
      rows.push(row);
      onWeekLoaded?.({ periodKey: meta.periodKey, outwardLines: rows.length });
    },
    { maxWeeks }
  );
  return { rows, weeksLoaded, outwardCount: matchCount };
}
